// API bridge: the UI was written against the old Next.js route handlers under
// `/api/*`. This module installs a `fetch` wrapper that maps those calls onto
// Laravel paths, attaches the tenant host and the session bearer token, and
// returns a clear 501 for any route the API does not serve. Components stay
// untouched.
//
// Every call stays on the origin that served the SPA. Vite (dev and preview)
// and the production web server forward /api and /storage to Laravel, so the
// browser never sees the API host and no CORS preflight is needed.

import { currentTenantHost } from '@/config';
import { currentImpersonation, impersonationHost } from '@/lib/platform/impersonation';
import { customerSession, customerShouldRemember, staffSession } from '@/lib/session';
import { parseStorefront, type StorefrontDocument } from '@/lib/storefront/document';
import { parsePublicTheme, type PublicTheme } from '@/lib/storefront/theme-document';

type Mapping = {
  pattern: RegExp;
  to: (match: RegExpMatchArray) => string;
  /** Optional per-request method override (the Next path implies it). */
  method?: string;
};

// Next path -> Laravel path. Order matters: first match wins.
const MAPPINGS: Mapping[] = [
  // Public catalog, leads and booking.
  { pattern: /^\/api\/leads$/, to: () => '/api/leads' },
  { pattern: /^\/api\/vehicles$/, to: () => '/api/vehicles' },
  { pattern: /^\/api\/vehicles\/([^/]+)\/photos$/, to: (m) => `/api/vehicles/${m[1]}/photos` },
  { pattern: /^\/api\/vehicles\/([^/]+)\/blocks\/([^/]+)$/, to: (m) => `/api/vehicles/${m[1]}/blocks/${m[2]}` },
  { pattern: /^\/api\/vehicles\/([^/]+)\/blocks$/, to: (m) => `/api/vehicles/${m[1]}/blocks` },
  { pattern: /^\/api\/vehicles\/([^/]+)$/, to: (m) => `/api/vehicles/${m[1]}` },
  { pattern: /^\/api\/quote$/, to: () => '/api/quotes' },
  { pattern: /^\/api\/book-and-sign$/, to: () => '/api/book-and-sign' },
  { pattern: /^\/api\/availability\/fleet$/, to: () => '/api/availability/fleet' },
  { pattern: /^\/api\/availability$/, to: () => '/api/availability' },

  // Storefront. The desk reads and writes its draft copy; visitors get the
  // published copy through the public-settings composition below.
  { pattern: /^\/api\/storefront\/publish$/, to: () => '/api/staff/storefront/publish' },
  { pattern: /^\/api\/storefront\/revert$/, to: () => '/api/staff/storefront/revert' },
  { pattern: /^\/api\/storefront$/, to: () => '/api/staff/storefront' },
  { pattern: /^\/api\/staff\/storefront\/(publish|revert)$/, to: (m) => `/api/staff/storefront/${m[1]}` },
  { pattern: /^\/api\/staff\/storefront$/, to: () => '/api/staff/storefront' },

  // Agency profile and brand image uploads.
  { pattern: /^\/api\/agency\/branding$/, to: () => '/api/agency/branding' },
  { pattern: /^\/api\/agency\/import-snapshot$/, to: () => '/api/agency/import-snapshot' },
  { pattern: /^\/api\/staff\/agency$/, to: () => '/api/staff/agency' },
  { pattern: /^\/api\/agency$/, to: () => '/api/agency' },

  // Public published pages and draft preview. Writes go through the platform
  // tenant routes below; there is no staff site-config write path.
  { pattern: /^\/api\/site-config\/([^/]+)$/, to: (m) => `/api/site-config/${m[1]}` },
  { pattern: /^\/api\/site-preview\/([^/]+)$/, to: (m) => `/api/site-preview/${m[1]}` },
  { pattern: /^\/api\/sitemap\.xml$/, to: () => '/api/sitemap.xml' },
  { pattern: /^\/api\/robots\.txt$/, to: () => '/api/robots.txt' },

  // Staff desk data.
  { pattern: /^\/api\/staff\/vehicles\/([^/]+)$/, to: (m) => `/api/staff/vehicles/${m[1]}` },
  { pattern: /^\/api\/staff\/vehicles$/, to: () => '/api/staff/vehicles' },
  { pattern: /^\/api\/staff\/vehicle-options$/, to: () => '/api/staff/vehicle-options' },
  { pattern: /^\/api\/booking-options$/, to: () => '/api/booking-options' },
  { pattern: /^\/api\/desk\/limits$/, to: () => '/api/desk/limits' },
  { pattern: /^\/api\/desk\/day$/, to: () => '/api/desk/day' },
  { pattern: /^\/api\/desk\/finance$/, to: () => '/api/desk/finance' },
  { pattern: /^\/api\/tariffs$/, to: () => '/api/tariffs' },
  { pattern: /^\/api\/clients\/([^/]+)$/, to: (m) => `/api/clients/${m[1]}` },
  { pattern: /^\/api\/clients$/, to: () => '/api/clients' },
  { pattern: /^\/api\/expenses\/([^/]+)$/, to: (m) => `/api/expenses/${m[1]}` },
  { pattern: /^\/api\/expenses$/, to: () => '/api/expenses' },
  { pattern: /^\/api\/fines\/([^/]+)$/, to: (m) => `/api/fines/${m[1]}` },
  { pattern: /^\/api\/fines$/, to: () => '/api/fines' },
  { pattern: /^\/api\/cash\/([^/]+)$/, to: (m) => `/api/cash/${m[1]}` },
  { pattern: /^\/api\/cash$/, to: () => '/api/cash' },

  // Staff accounts.
  { pattern: /^\/api\/users\/invitations\/([^/]+)$/, to: (m) => `/api/users/invitations/${m[1]}` },
  { pattern: /^\/api\/users\/invitations$/, to: () => '/api/users/invitations' },
  { pattern: /^\/api\/users\/([^/]+)$/, to: (m) => `/api/users/${m[1]}` },
  { pattern: /^\/api\/users$/, to: () => '/api/users' },

  // Bookings and contracts.
  { pattern: /^\/api\/bookings\/([^/]+)\/contracts$/, to: (m) => `/api/bookings/${m[1]}/contracts` },
  { pattern: /^\/api\/bookings\/([^/]+)$/, to: (m) => `/api/bookings/${m[1]}` },
  { pattern: /^\/api\/bookings$/, to: () => '/api/bookings' },
  { pattern: /^\/api\/contracts\/([^/]+)\/download$/, to: (m) => `/api/contracts/${m[1]}/file` },
  { pattern: /^\/api\/contracts\/([^/]+)\/(send|countersign|cancel|supersede)$/, to: (m) => `/api/contracts/${m[1]}/${m[2]}` },
  { pattern: /^\/api\/contracts\/([^/]+)$/, to: (m) => `/api/contracts/${m[1]}` },

  // Anonymous signer. The token is in the path; Laravel scopes it to this tenant.
  { pattern: /^\/api\/sign\/([^/]+)\/(request-code|verify-code|complete|download)$/, to: (m) => `/api/sign/${m[1]}/${m[2]}` },
  { pattern: /^\/api\/sign\/([^/]+)$/, to: (m) => `/api/sign/${m[1]}` },

  // Customer accounts map onto Laravel customers.
  { pattern: /^\/api\/customers\/email-verifications\/([^/]+)$/, to: (m) => `/api/customers/email-verifications/${m[1]}` },
  { pattern: /^\/api\/account\/password\/forgot$/, to: () => '/api/customers/password/forgot' },
  { pattern: /^\/api\/account\/password\/reset$/, to: () => '/api/customers/password/reset' },
  { pattern: /^\/api\/account\/password$/, to: () => '/api/customers/password' },
  { pattern: /^\/api\/account\/email\/otp\/confirm$/, to: () => '/api/customers/email/otp/confirm' },
  { pattern: /^\/api\/account\/email\/otp$/, to: () => '/api/customers/email/otp' },
  { pattern: /^\/api\/account\/email\/verify$/, to: () => '/api/customers/email/verify' },
  { pattern: /^\/api\/account\/phone\/verify$/, to: () => '/api/customers/phone/verify' },
  { pattern: /^\/api\/account\/phone\/resend$/, to: () => '/api/customers/phone/resend' },
  { pattern: /^\/api\/account\/avatar$/, to: () => '/api/customers/avatar' },
  { pattern: /^\/api\/account\/register$/, to: () => '/api/customers' },
  { pattern: /^\/api\/account\/login$/, to: () => '/api/customers/login' },
  { pattern: /^\/api\/account\/logout$/, to: () => '/api/customers/session', method: 'DELETE' },
  { pattern: /^\/api\/account\/bookings\/([^/]+)\/cancel$/, to: (m) => `/api/customers/bookings/${m[1]}/cancel` },
  { pattern: /^\/api\/account\/bookings\/([^/]+)$/, to: (m) => `/api/customers/bookings/${m[1]}` },
  { pattern: /^\/api\/account\/bookings$/, to: () => '/api/customers/bookings' },
  { pattern: /^\/api\/account$/, to: () => '/api/customers/session' },

  // CSRF bootstrap (session cookie + token for mutating calls).
  { pattern: /^\/api\/csrf-token$/, to: () => '/api/csrf-token' },

  // Platform session routes. These stay off the tenant connection.
  // Must come before the generic /api/auth/* mapping.
  { pattern: /^\/api\/platform\/auth\/(.+)$/, to: (m) => `/api/platform/auth/${m[1]}` },
  { pattern: /^\/api\/platform\/usage$/, to: () => '/api/platform/usage' },
  { pattern: /^\/api\/platform\/impersonation\/([^/]+)$/, to: (match) => `/api/platform/impersonation/${match[1]}` },
  { pattern: /^\/api\/platform\/impersonation$/, to: () => '/api/platform/impersonation' },
  { pattern: /^\/api\/platform\/plans\/([^/]+)$/, to: (match) => `/api/platform/plans/${match[1]}` },
  { pattern: /^\/api\/platform\/plans$/, to: () => '/api/platform/plans' },
  { pattern: /^\/api\/platform\/features$/, to: () => '/api/platform/features' },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/entitlements$/, to: (match) => `/api/platform/tenants/${match[1]}/entitlements` },
  { pattern: /^\/api\/platform\/audits$/, to: () => '/api/platform/audits' },
  { pattern: /^\/api\/platform\/control\/tenants\/([^/]+)\/(recheck|backup)$/, to: (match) => `/api/platform/control/tenants/${match[1]}/${match[2]}` },
  { pattern: /^\/api\/platform\/control\/jobs\/([^/]+)\/retry$/, to: (match) => `/api/platform/control/jobs/${match[1]}/retry` },
  { pattern: /^\/api\/platform\/control\/cache\/settings$/, to: () => '/api/platform/control/cache/settings' },
  { pattern: /^\/api\/platform\/control$/, to: () => '/api/platform/control' },
  { pattern: /^\/api\/platform\/settings\/secrets\/([^/]+)$/, to: (match) => `/api/platform/settings/secrets/${match[1]}` },
  { pattern: /^\/api\/platform\/settings\/test\/([^/]+)$/, to: (match) => `/api/platform/settings/test/${match[1]}` },
  { pattern: /^\/api\/platform\/settings\/(health|history)$/, to: (match) => `/api/platform/settings/${match[1]}` },
  { pattern: /^\/api\/platform\/settings\/([^/]+)$/, to: (match) => `/api/platform/settings/${match[1]}` },
  { pattern: /^\/api\/platform\/settings$/, to: () => '/api/platform/settings' },
  { pattern: /^\/api\/platform\/overview$/, to: () => '/api/platform/overview' },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/theme-document\/(publish|revert|preset)$/, to: (match) => `/api/platform/tenants/${match[1]}/theme-document/${match[2]}` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/theme-document$/, to: (match) => `/api/platform/tenants/${match[1]}/theme-document` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/storefront\/(publish|revert)$/, to: (match) => `/api/platform/tenants/${match[1]}/storefront/${match[2]}` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/storefront$/, to: (match) => `/api/platform/tenants/${match[1]}/storefront` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/site-pages$/, to: (match) => `/api/platform/tenants/${match[1]}/site-pages` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/site-preview$/, to: (match) => `/api/platform/tenants/${match[1]}/site-preview` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/site-config\/([^/]+)\/(publish|revert)$/, to: (match) => `/api/platform/tenants/${match[1]}/site-config/${match[2]}/${match[3]}` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/site-config\/([^/]+)\/draft$/, to: (match) => `/api/platform/tenants/${match[1]}/site-config/${match[2]}/draft` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/site-config\/([^/]+)$/, to: (match) => `/api/platform/tenants/${match[1]}/site-config/${match[2]}` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/site-config$/, to: (match) => `/api/platform/tenants/${match[1]}/site-config` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/site-images$/, to: (match) => `/api/platform/tenants/${match[1]}/site-images` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/domain\/check$/, to: (match) => `/api/platform/tenants/${match[1]}/domain/check` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)\/(health|migrate|backup|theme|profile|branding|domain|vehicles)$/, to: (match) => `/api/platform/tenants/${match[1]}/${match[2]}` },
  { pattern: /^\/api\/platform\/tenants\/([^/]+)$/, to: (match) => `/api/platform/tenants/${match[1]}` },
  { pattern: /^\/api\/platform\/tenants$/, to: () => '/api/platform/tenants' },
  { pattern: /^\/api\/platform\/users\/([^/]+)$/, to: (match) => `/api/platform/users/${match[1]}` },
  { pattern: /^\/api\/platform\/users$/, to: () => '/api/platform/users' },
  { pattern: /^\/api\/platform\/staff\/([^/]+)\/invitations\/([^/]+)$/, to: (match) => `/api/platform/staff/${match[1]}/invitations/${match[2]}` },
  { pattern: /^\/api\/platform\/staff\/([^/]+)\/([^/]+)$/, to: (match) => `/api/platform/staff/${match[1]}/${match[2]}` },
  { pattern: /^\/api\/platform\/staff\/([^/]+)$/, to: (match) => `/api/platform/staff/${match[1]}` },
  { pattern: /^\/api\/platform\/staff$/, to: () => '/api/platform/staff' },

  // Agency staff auth. Platform auth is mapped above as /api/platform/auth/*.
  { pattern: /^\/api\/auth\/(.+)$/, to: (m) => `/api/auth/${m[1]}` },

  // Tenant entitlements for the desk console.
  { pattern: /^\/api\/entitlements$/, to: () => '/api/entitlements' },

  // Landing page demo requests (public, central).
  { pattern: /^\/api\/contact$/, to: () => '/api/contact' },
  { pattern: /^\/api\/places\/reverse$/, to: () => '/api/places/reverse' },
  { pattern: /^\/api\/places\/search$/, to: () => '/api/places/search' },
  { pattern: /^\/api\/places\/nearby$/, to: () => '/api/places/nearby' },
  { pattern: /^\/api\/assistant$/, to: () => '/api/assistant' },
];

function mapPath(pathname: string): { target: string; method?: string } | null {
  for (const mapping of MAPPINGS) {
    const match = pathname.match(mapping.pattern);
    if (match) return { target: mapping.to(match), method: mapping.method };
  }
  return null;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const CSRF_HEADER = 'X-CSRF-TOKEN';

let csrfToken: string | null = null;
let csrfPromise: Promise<string | null> | null = null;

function clearCsrfToken(): void {
  csrfToken = null;
  csrfPromise = null;
}

async function ensureCsrfToken(originalFetch: typeof fetch, forceRefresh = false): Promise<string | null> {
  if (forceRefresh) clearCsrfToken();
  if (csrfToken) return csrfToken;
  if (!csrfPromise) {
    csrfPromise = (async () => {
      const response = await originalFetch('/api/csrf-token', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const body = (await response.json().catch(() => null)) as { csrfToken?: unknown } | null;
      const token = typeof body?.csrfToken === 'string' ? body.csrfToken : null;
      csrfToken = token;
      return token;
    })().finally(() => {
      csrfPromise = null;
    });
  }
  return csrfPromise;
}

function applyHeaders(headers: Headers, pathname = '', method = 'GET'): void {
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (csrfToken && !SAFE_METHODS.has(method.toUpperCase())) {
    headers.set(CSRF_HEADER, csrfToken);
  }
  const impersonation = currentImpersonation();
  const endsImpersonation = pathname === '/api/platform/impersonation' && method === 'DELETE';
  const deskCall = impersonation !== null && (endsImpersonation || !pathname.startsWith('/api/platform/'));
  if (deskCall && impersonation) {
    headers.set('X-Tenant-Host', impersonationHost(impersonation.slug));
    headers.set('Authorization', `Bearer ${impersonation.token}`);
    return;
  }
  // Platform routes use the central connection, so X-Tenant-Host is
  // meaningless there.
  if (!pathname.startsWith('/api/platform/')) {
    headers.set('X-Tenant-Host', currentTenantHost());
  }
  const staff = staffSession.token();
  const customer = customerSession.token();
  if (staff) headers.set('Authorization', `Bearer ${staff}`);
  else if (customer) headers.set('Authorization', `Bearer ${customer}`);
  // Customer routes authenticate with their own opaque header.
  if (customer) headers.set('X-Customer-Token', customer);
}

/**
 * The Next app kept the customer session in an httpOnly cookie set by the
 * server. With no server, we capture the token the sign-in endpoints return and
 * store it client-side so later requests (and page reloads) stay authenticated.
 */
async function captureCustomerSession(response: Response): Promise<void> {
  const body = (await response.clone().json().catch(() => null)) as
    | { token?: unknown; customer?: unknown }
    | null;
  const token = body?.token;
  if (typeof token !== 'string' || token === '') return;
  customerSession.set({ token, customer: body?.customer as never }, customerShouldRemember());
}

interface PublicSettings {
  currency: string;
  rateEur: number;
  rateUsd: number;
  name: string;
  shortName: string;
  city: string;
  address: string;
  phone: string;
  whatsapp: string;
  email: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  localizedContact: Record<string, unknown>;
  storefront: StorefrontDocument | null;
  theme: PublicTheme | null;
}

// The Next app had a single /api/public-settings endpoint. Laravel exposes the
// same data as /api/agency plus /api/storefront, so compose it here.
async function composePublicSettings(originalFetch: typeof fetch): Promise<Response> {
  try {
    const headers = new Headers();
    applyHeaders(headers);
    const [agencyRes, storefrontRes] = await Promise.all([
      originalFetch('/api/agency', { headers }),
      originalFetch('/api/storefront', { headers }),
    ]);
    // A paused or unknown tenant must not look like an empty agency. Propagate
    // 403/404 so the public site can show the paused shell instead of Classic.
    if (agencyRes.status === 403 || agencyRes.status === 404) {
      return jsonResponse({ error: 'Agency unavailable' }, agencyRes.status);
    }
    if (!agencyRes.ok) {
      return jsonResponse({ error: 'Public settings unavailable' }, agencyRes.status >= 500 ? 502 : agencyRes.status);
    }
    const agency = (await agencyRes.json()) as Record<string, unknown>;
    const storefrontBody = storefrontRes.ok
      ? ((await storefrontRes.json()) as { document?: unknown; theme?: unknown })
      : {};
    const settings: PublicSettings = {
      currency: typeof agency.currency === 'string' ? agency.currency : 'MAD',
      rateEur: typeof agency.rateEur === 'number' && agency.rateEur > 0 ? agency.rateEur : 10.8,
      rateUsd: typeof agency.rateUsd === 'number' && agency.rateUsd > 0 ? agency.rateUsd : 10.0,
      name: typeof agency.name === 'string' ? agency.name : '',
      shortName: typeof agency.shortName === 'string' ? agency.shortName : '',
      city: typeof agency.city === 'string' ? agency.city : '',
      address: typeof agency.address === 'string' ? agency.address : '',
      phone: typeof agency.phone === 'string' ? agency.phone : '',
      whatsapp: typeof agency.whatsapp === 'string' ? agency.whatsapp : '',
      email: typeof agency.email === 'string' ? agency.email : '',
      logoUrl: '',
      faviconUrl: '',
      primaryColor: typeof agency.primaryColor === 'string' ? agency.primaryColor : '',
      secondaryColor: typeof agency.secondaryColor === 'string' ? agency.secondaryColor : '',
      localizedContact: {},
      storefront: parseStorefront(storefrontBody.document),
      theme: parsePublicTheme(storefrontBody.theme),
    };
    return jsonResponse(settings, 200);
  } catch {
    return jsonResponse({ error: 'Public settings unavailable' }, 502);
  }
}

function jsonResponse(data: unknown, status = 200): Response {
  return new Response(JSON.stringify(data), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

let installed = false;

export function installApiBridge(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const rawUrl =
      typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

    // Only rewrite same-origin /api/* calls.
    if (!rawUrl.startsWith('/api/')) return originalFetch(input, init);

    const [pathname, search = ''] = rawUrl.split('?');
    const query = search ? `?${search}` : '';

    if (pathname === '/api/public-settings') {
      return composePublicSettings(originalFetch);
    }

    const mapped = mapPath(pathname);
    if (!mapped) {
      return jsonResponse(
        {
          error: `This endpoint is not available yet: ${pathname}. It still needs a Laravel route (see MIGRATION.md).`,
        },
        501,
      );
    }

    const method = (mapped.method ?? init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const needsCsrf = !SAFE_METHODS.has(method) && pathname !== '/api/csrf-token';

    const send = async (forceCsrfRefresh = false): Promise<Response> => {
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
      if (needsCsrf) {
        await ensureCsrfToken(originalFetch, forceCsrfRefresh);
      }
      applyHeaders(headers, pathname, method);
      return originalFetch(`${mapped.target}${query}`, {
        ...init,
        method,
        headers,
        credentials: 'same-origin',
      });
    };

    let response = await send(false);
    if (needsCsrf && response.status === 419) {
      response = await send(true);
    }

    if (pathname === '/api/account/logout') {
      customerSession.set(null);
    } else if (response.ok && (pathname === '/api/account/login' || pathname === '/api/account/register' || pathname === '/api/account/phone/verify')) {
      await captureCustomerSession(response);
    }

    return response;
  };
}
